import { useHead } from '@unhead/react'
import { isHttpImageUrl } from '@/lib/tokenImage'

export const PENGUIN_PAD_OG_IMAGE =
  'https://static.lumi.new/94/942a6f0e26fab3b022a1af33a7354cdb.webp?v=4'

const OG_IMAGE_ALT = 'Penguin Pad — fair-launch bonding curves and community presales'
const CANONICAL_ORIGIN = 'https://penguinpad.xyz'
const DEFAULT_OG_WIDTH = '1200'
const DEFAULT_OG_HEIGHT = '630'
const BANNER_OG_WIDTH = '1600'
const BANNER_OG_HEIGHT = '400'

export function shareCardImage(banner?: string | null): string {
  const value = banner?.trim()
  return value && isHttpImageUrl(value) ? value : PENGUIN_PAD_OG_IMAGE
}

function imageMimeType(url: string): string {
  const path = url.split('?')[0]?.toLowerCase() ?? ''
  if (path.endsWith('.png'))
    return 'image/png'
  if (path.endsWith('.jpg') || path.endsWith('.jpeg'))
    return 'image/jpeg'
  if (path.endsWith('.gif'))
    return 'image/gif'
  return 'image/webp'
}

interface SeoHeadProps {
  title: string
  description: string
  image?: string
  imageAlt?: string
  url?: string
}

function SeoHead({
  title,
  description,
  image = PENGUIN_PAD_OG_IMAGE,
  imageAlt = OG_IMAGE_ALT,
  url = `${CANONICAL_ORIGIN}/`,
}: SeoHeadProps) {
  const pageTitle = `${title} | Penguin Pad`
  const cardImage = shareCardImage(image)
  const usingBanner = cardImage !== PENGUIN_PAD_OG_IMAGE
  const width = usingBanner ? BANNER_OG_WIDTH : DEFAULT_OG_WIDTH
  const height = usingBanner ? BANNER_OG_HEIGHT : DEFAULT_OG_HEIGHT
  const alt = usingBanner ? imageAlt : OG_IMAGE_ALT

  useHead({
    title: pageTitle,
    link: [
      { rel: 'canonical', href: url },
      { rel: 'image_src', href: cardImage },
    ],
    meta: [
      { name: 'description', content: description },
      { property: 'og:title', content: pageTitle },
      { property: 'og:description', content: description },
      { property: 'og:type', content: 'website' },
      { property: 'og:url', content: url },
      { property: 'og:site_name', content: 'Penguin Pad' },
      { property: 'og:image', content: cardImage },
      { property: 'og:image:secure_url', content: cardImage },
      { property: 'og:image:alt', content: alt },
      { property: 'og:image:type', content: imageMimeType(cardImage) },
      { property: 'og:image:width', content: width },
      { property: 'og:image:height', content: height },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:site', content: '@PenguinPad_xyz' },
      { name: 'twitter:title', content: pageTitle },
      { name: 'twitter:description', content: description },
      { name: 'twitter:url', content: url },
      { name: 'twitter:image', content: cardImage },
      { name: 'twitter:image:src', content: cardImage },
      { name: 'twitter:image:alt', content: alt },
    ],
  })
  return null
}

export default SeoHead
